/**
 * @typedef {import('../index').Character} Character
 * @typedef {import('../index').Place} Place
 * @typedef {import('../index').Note} Note
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').LegacyCustomAttribute} LegacyCustomAttribute
 * @typedef {import('../index').Attribute} Attribute
 */

/**
 * You can change a paragraph type back to text if:
 *  1. It has no value
 *  2. It is a string
 *  3. It's value is the same as RCE_INITIAL_VALUE
 *  4. It's value is an empty paragraph
 *  Otherwise, a paragraph type can not be changed back
 *
 *  @param {Character | Place | Note | Card} item
 *  @param {String} attr
 *  @return {boolean}
 */
export function hasNoLegacyValue(item, attr) {
  const val = item[attr]
  if (!val) return true
  if (typeof val === 'string') return true

  if (!val.length) return true
  if (val.length > 1) return false // more than 1 paragraph
  if (val[0].children) {
    if (val[0].children.length > 1) return false // more than 1 text node
    if (val[0].children[0].text == '') return true // no text
  }
  return false
}

/**
 * @param {Array<Character> | Array<Place> | Array<Note> | Array<Card>} entities
 * @param {Array<LegacyCustomAttribute>} attrs
 * @return {Array<String>}
 */
export const noEntityHasLegacyAttributeBound = (entities, attrs) => {
  return attrs.reduce((acc, attr) => {
    if (!attr) {
      return acc
    } else if (attr.type == 'text') {
      return [...acc, attr.name]
    } else {
      const changeable = entities.every((ch) => hasNoLegacyValue(ch, attr.name))
      if (changeable) {
        return [...acc, attr.name]
      } else {
        return acc
      }
    }
  }, [])
}

/**
 * You can change a paragraph type back to text if:
 *  1. It has no value
 *  2. It is a string
 *  3. It's value is the same as RCE_INITIAL_VALUE
 *  4. It's value is an empty paragraph
 * Otherwise, a paragraph type can not be changed back
 *
 * @param {Character | Place | Note | Card} item
 * @param {number} id
 * @return {boolean}
 */
export function hasNoValue(item, id) {
  const attributeValues = 'attributes' in item ? item.attributes : []
  const val = attributeValues.find((item) => {
    return item.id === id
  })?.value
  if (!val) return true
  if (typeof val === 'string') return true

  if (Array.isArray(val)) {
    // NOTE(ed): In this case, we're dealing with a list of numbers,
    // e.g. associated tags.
    if (val.every((x) => typeof x === 'number')) return true
    if (!val.length) return true
    if (val.length > 1) return false // more than 1 paragraph
    if (val[0].children) {
      if (val[0].children.length > 1) return false // more than 1 text node
      if (val[0].children[0].text == '') return true // no text
    }
  }
  return false
}

/**
 * @param {Array<Character> | Array<Place> | Array<Note> | Array<Card>} entities
 * @param {Array<Attribute>} attrs
 * @return {Array<String>}
 */
export const noEntityHasAttributeBound = (entities, attrs) => {
  return attrs.reduce((acc, attr) => {
    if (!attr) {
      return acc
    } else if (attr.type === 'base-attribute') {
      return acc
    } else if (attr.type == 'text') {
      return [...acc, attr.name]
    } else {
      const changeable = entities.every((ch) => hasNoValue(ch, attr.id))
      if (changeable) {
        return [...acc, attr.name]
      } else {
        return acc
      }
    }
  }, [])
}
